import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TikTokCreatorBlock, TikTokSettingsIssue } from 'fym-shared/src/music24/tiktokPostSettings';
import { TypedMemo } from '../../../../common/types';
import { clockLabel } from './clockLabel';
import { tiktokMessages } from './tiktokMessages';
import { withBrandWord } from '../../../components/primitives/BrandWord';

interface TikTokBlockNotesProps {
  /** Why the creator cannot post now, or null. */
  blockedReason: TikTokCreatorBlock | null;
  issues: TikTokSettingsIssue[];
  durationSec: number | null;
  maxDurationSec: number | null;
  /** The owner of the workspace can open the plans. */
  canSeePlans: boolean;
  onCheckAgain: () => void;
  onOpenChannels: () => void;
  onSeePlans: () => void;
}

const BLOCK_MESSAGES = {
  tooManyPosts: tiktokMessages.blockTooManyPosts,
  bannedFromPosting: tiktokMessages.blockBanned,
  activeUserCap: tiktokMessages.blockActiveUserCap,
  noPrivacyOptions: tiktokMessages.blockNoPrivacy,
  reconnect: tiktokMessages.blockReconnect,
  unavailable: tiktokMessages.blockUnavailable,
} as const;

/**
 * The reasons that TikTok stops the post and that the user cannot fix with a setting (design a3):
 * the creator cannot post now (G2), the login stopped, the file has the Free plan mark (G9) and
 * the video is longer than the account takes (G2, G7).
 */
const TikTokBlockNotesComponent: React.FC<TikTokBlockNotesProps> = ({
  blockedReason,
  issues,
  durationSec,
  maxDurationSec,
  canSeePlans,
  onCheckAgain,
  onOpenChannels,
  onSeePlans,
}) => {
  const intl = useIntl();
  const check = useCallback(() => onCheckAgain(), [onCheckAgain]);

  return (
    <>
      {blockedReason === null ? null : (
        <Alert color="danger" className="mb-0" data-testid="tiktok-blocked">
          {withBrandWord(intl.formatMessage(BLOCK_MESSAGES[blockedReason]))}{' '}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={check}>
            {intl.formatMessage(tiktokMessages.checkAgain)}
          </button>
          {blockedReason === 'reconnect' ? (
            <>
              {' '}
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={onOpenChannels}>
                {intl.formatMessage(tiktokMessages.openChannels)}
              </button>
            </>
          ) : null}
        </Alert>
      )}
      {issues.includes('watermarked') ? (
        <Alert color="danger" className="mb-0" data-testid="tiktok-watermarked">
          {withBrandWord(intl.formatMessage(tiktokMessages.blockWatermark))}
          {canSeePlans ? (
            <>
              {' '}
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={onSeePlans}>
                {intl.formatMessage(tiktokMessages.seePlans)}
              </button>
            </>
          ) : null}
        </Alert>
      ) : null}
      {issues.includes('videoTooLong') && durationSec !== null && maxDurationSec !== null ? (
        <Alert color="danger" className="mb-0" data-testid="tiktok-too-long">
          {intl.formatMessage(tiktokMessages.blockTooLong, {
            length: clockLabel(durationSec),
            max: clockLabel(maxDurationSec),
          })}
        </Alert>
      ) : null}
    </>
  );
};

export const TikTokBlockNotes = TypedMemo(TikTokBlockNotesComponent);
